import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import AlertModal from "@/components/shared/AlertModal";
import { JOB_STATUS } from "@/config/dropdownData";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/dateUtils";
import OneLineSkills from "@/components/shared/OneLineSkills";
import { CircleX, Copy, Eye, FileText, ChevronDown, ChevronUp, Loader2, MoreVertical, Pencil, Power, QrCode, UploadCloud } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import CandidateSection from "./CandidateSection";
import { useNavigate } from "react-router-dom";
import JobPriorityBadge from "./JobPriorityBadge";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import ModuleGate from "@/permissions/ModuleGate";
import { MODULES } from "@/permissions/modules";
import CustomTooltip from "@/components/shared/CustomTooltip";
import ShareJobQrModal from "@/components/shared/ShareJobQrModal";

const DROPDOWN_WIDTH = 224;
const DROPDOWN_GAP = 4;
const ESTIMATED_ITEM_HEIGHT = 42;
const MAX_DROPDOWN_ITEMS = 7;

const JobCard = ({
  job = {},
  viewMode = "list",
  isDataUpdate = false,
  handleEdit = () => {},
  handleClone = () => {},
  handleStatusChange = () => {},
  handleCandidateList = () => {},
  candidatesData = {},
  candidatesLoading = {},
}) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [resumesOpen, setResumesOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [shareQrOpen, setShareQrOpen] = useState(false);

  const isOpen = job?.status?.toLowerCase() === JOB_STATUS.JOB_OPEN.toLowerCase();

  const creatorName = job?.created_by_details?.name || job?.created_by_details?.email;
  const activeAssignments = job?.assignments || [];
  const assigneeName = activeAssignments[0]?.name || activeAssignments[0]?.email;
  const extraAssigneeCount = activeAssignments.length > 1 ? activeAssignments.length - 1 : 0;

  const createdByValue = creatorName || "—";
  const assignedToValue = assigneeName ? `${assigneeName}${extraAssigneeCount ? ` +${extraAssigneeCount}` : ""}` : "—";

  const skills = job?.skills || [];
  const requestStatusChange = () => setShowStatusConfirm(true);

  const confirmStatusChange = () => {
    setShowStatusConfirm(false);
    handleStatusChange(job?.id);
  };

  // Location leads, because job titles are no longer unique per tenant: two
  // cards reading "Python Developer" are told apart by where the role is.
  const jobStats = [
    {
      label: "Location",
      value: job?.location || "—",
      href: job?.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location)}` : null,
    },
    { label: "Shift", value: job?.shift_timing || "—" },
    { label: "Calendar", value: job?.calendar_type_name || "—" },
    { label: "Applicants", value: job?.candidate_count ?? 0 },
    { label: "Due Date", value: formatDate(job?.deadline) },
    { label: "Job Posted", value: formatDate(job?.created_at) },
    { label: "Total Openings", value: job?.vacancy_count ?? 0 },
    { label: "Positions Closed", value: job?.vacancy_count - job?.remaining_vacancies ?? 0 },
    { label: "Available", value: job?.remaining_vacancies ?? 0 },
    { label: "Created By", value: createdByValue },
    { label: "Assigned To", value: assignedToValue },
  ];

  const handleToggleResumes = () => {
    const next = !resumesOpen;
    setResumesOpen(next);
    if (next && !candidatesData[job?.id]) {
      handleCandidateList(job?.id);
    }
  };

  // The card list this renders inside scrolls its own container
  // (`overflow-y-auto` in job/index.jsx) — a plain `position: absolute`
  // dropdown gets clipped by that ancestor the moment a card sits near the
  // bottom of the visible scroll area, regardless of how much room is left
  // in the actual browser viewport. Portaling to `document.body` with
  // `position: fixed`, computed from the trigger's own bounding rect (same
  // approach as StatusBarDropdown.jsx), escapes that clipping entirely.
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const estimatedHeight = MAX_DROPDOWN_ITEMS * ESTIMATED_ITEM_HEIGHT;
    const spaceBelow = viewportHeight - rect.bottom - DROPDOWN_GAP;
    const spaceAbove = rect.top - DROPDOWN_GAP;
    const openBelow = spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;

    let left = rect.right - DROPDOWN_WIDTH;
    if (left < 8) left = 8;
    if (left + DROPDOWN_WIDTH > viewportWidth - 8) {
      left = viewportWidth - DROPDOWN_WIDTH - 8;
    }

    // No maxHeight/overflow cap here on purpose — the menu's own content
    // (~7 short rows) essentially never needs internal scrolling, and
    // `overflow-y-auto` clips the disabled item's tooltip (which pokes out
    // above the box) while also promoting overflow-x to auto per the CSS
    // spec, producing a stray horizontal scrollbar. openBelow/openAbove still
    // picks whichever side actually has more room.
    const style = {
      position: "fixed",
      left,
      width: DROPDOWN_WIDTH,
      zIndex: 9999,
    };

    if (openBelow) {
      style.top = rect.bottom + DROPDOWN_GAP;
    } else {
      style.bottom = viewportHeight - rect.top + DROPDOWN_GAP;
    }

    setDropdownStyle(style);
  }, []);

  useEffect(() => {
    if (dropdownOpen) calculatePosition();
  }, [dropdownOpen, calculatePosition]);

  useEffect(() => {
    if (!dropdownOpen) return;
    window.addEventListener("resize", calculatePosition);
    return () => window.removeEventListener("resize", calculatePosition);
  }, [dropdownOpen, calculatePosition]);

  // Close dropdown when clicking outside (either the trigger or the
  // portaled menu itself — the menu no longer lives inside this card's DOM
  // subtree, so both refs have to be checked).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!triggerRef.current?.contains(event.target) && !dropdownRef.current?.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scrolling the list (or the page) while open would leave a fixed-position
  // menu visually detached from its trigger — closing it is simpler and
  // matches the existing StatusBarDropdown convention.
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleScroll = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [dropdownOpen]);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const handleViewDetail = () => navigate(`/admin/job-detail/${job?.id}`);

  const handleAction = (action) => {
    setDropdownOpen(false);
    if (action === "view") handleViewDetail();
    if (action === "edit" && isOpen) handleEdit(job?.id);
    if (action === "clone") handleClone(job?.id);
    if (action === "status") requestStatusChange();
    if (action === "bulkUpload" && isOpen) handleBulkUpload();
  };
  const handleBulkUpload = () => navigate(`/admin/job-detail/${job?.id}/bulk-upload`);
  return (
    <Card
      className={cn(
        "bg-background-secondary border border-border-primary/65 text-text-primary rounded-xl !p-0 ",
        "flex flex-col h-full",
        isDataUpdate && "opacity-70 pointer-events-none",
      )}
    >
      {/* ── Main content area ── */}
      <div className="flex-1 px-4 pt-3.5 pb-3 flex flex-col">
        {/* ── Row 1: Title + Status + Actions ── */}
        <div className="flex items-start justify-between gap-3 relative">
          {/* Left: title + status pill */}
          <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
            <h2
              onClick={handleViewDetail}
              className="text-sm font-semibold leading-tight text-text-primary line-clamp-2 pr-2 cursor-pointer hover:text-text-blue transition-colors"
            >
              {job?.title ?? "No Title"}
            </h2>

            <JobPriorityBadge priority={job?.job_priority} />
            <div className={cn("rounded-full border", isOpen ? "border-tone-emerald" : "border-tone-rose")} style={{ width: 52, height: 24 }}>
              <span
                className={cn(
                  "flex items-center justify-center text-[11px] rounded-full bg-background-secondary w-full h-full gap-0.5",
                  isOpen ? "text-tone-emerald" : "text-tone-rose",
                )}
              >
                <span className="text-[8px]">●</span>
                {isOpen ? JOB_STATUS.JOB_OPEN : JOB_STATUS.JOB_CLOSE}
              </span>
            </div>

            {(job?.is_created_by_me || job?.is_assigned_to_me) && (
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${
                  job.is_created_by_me && job.is_assigned_to_me
                    ? "bg-tone-purple/10 border-tone-purple/30 text-tone-purple"
                    : job.is_created_by_me
                      ? "bg-tone-emerald/10 border-tone-emerald/30 text-tone-emerald"
                      : "bg-tone-blue/10 border-tone-blue/30 text-tone-blue"
                }`}
              >
                {job.is_created_by_me && job.is_assigned_to_me ? "Created & Assigned to me" : job.is_created_by_me ? "Created by me" : "Assigned to me"}
              </span>
            )}
          </div>

          {/* Actions menu — the only action surface, in both list and grid
              view. The bulk-upload entry inside it carries the module gate:
              `bulk_intake` owns no permission on purpose (the tenant still
              needs candidate.create for everything else), so permission
              filtering alone would leave the item visible. */}
          <Button
            ref={triggerRef}
            aria-label="Job actions"
            className="flex items-center justify-center flex-shrink-0 text-text-secondary p-1 hover:bg-background-tertiary rounded-md"
            onClick={toggleDropdown}
          >
            <MoreVertical size={16} />
          </Button>

          {/* Dropdown contents — portaled to escape the scrollable list's
              overflow clipping (see the comment above calculatePosition). */}
          {dropdownOpen &&
            createPortal(
              <div
                ref={dropdownRef}
                style={dropdownStyle}
                className="flex flex-col bg-background-form border border-border-primary rounded-xl shadow-xl px-1 py-1 text-sm"
              >
                <PermissionGuard permission={PERMISSIONS.JOB_VIEW} isComponent>
                  <Button
                    onClick={() => handleAction("view")}
                    className="flex items-center gap-3 text-text-primary w-full px-4 py-2.5 !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                  >
                    <Eye size={16} /> View Details
                  </Button>
                </PermissionGuard>

                <PermissionGuard permission={PERMISSIONS.JOB_VIEW} isComponent>
                  {isOpen ? (
                    <Button
                      onClick={() => {
                        setDropdownOpen(false);
                        setShareQrOpen(true);
                      }}
                      className="flex items-center gap-3 text-text-primary w-full px-4 py-2.5 !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                    >
                      <QrCode size={16} /> Share QR Code
                    </Button>
                  ) : (
                    <CustomTooltip text="This job is closed and cannot be shared" className="w-full">
                      <Button
                        disabled
                        className="flex items-center gap-3 text-text-primary w-full px-4 py-2.5 !rounded-md text-left opacity-50 cursor-not-allowed whitespace-nowrap"
                      >
                        <QrCode size={16} /> Share QR Code
                      </Button>
                    </CustomTooltip>
                  )}
                </PermissionGuard>

                <PermissionGuard permission={PERMISSIONS.JOB_UPDATE} isComponent>
                  {isOpen && (
                    <Button
                      onClick={() => handleAction("edit")}
                      className="flex items-center gap-3 w-full px-4 text-text-primary py-2.5 !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                    >
                      <Pencil size={16} /> Edit Job
                    </Button>
                  )}
                </PermissionGuard>

                <PermissionGuard permission={PERMISSIONS.JOB_CREATE} isComponent>
                  <Button
                    onClick={() => handleAction("clone")}
                    className="flex items-center gap-3 w-full px-4 text-text-primary py-2.5 !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                  >
                    <Copy size={16} /> Clone Job
                  </Button>
                </PermissionGuard>

                <PermissionGuard permission={PERMISSIONS.JOB_UPDATE} isComponent>
                  <Button
                    onClick={() => handleAction("status")}
                    disabled={isDataUpdate}
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-text-primary !rounded-md hover:bg-background-primary text-left disabled:opacity-50 whitespace-nowrap"
                  >
                    {isDataUpdate ? <Loader2 size={16} className="animate-spin" /> : isOpen ? <CircleX size={16} /> : <Power size={16} />}
                    {isDataUpdate ? "Loading..." : isOpen ? "Close Job" : "Reopen Job"}
                  </Button>
                </PermissionGuard>

                {isOpen && (
                  <PermissionGuard permission={PERMISSIONS.CANDIDATE_CREATE} isComponent>
                    {/* `w-full`: this item fills the dropdown's width, so the
                      disabled wrapper has to as well. */}
                    <ModuleGate module={MODULES.BULK_INTAKE} className="w-full">
                      <Button
                        onClick={() => handleAction("bulkUpload")}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-text-primary !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                      >
                        <UploadCloud size={16} /> Bulk Upload Resumes
                      </Button>
                    </ModuleGate>
                  </PermissionGuard>
                )}
              </div>,
              document.body,
            )}
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1 mt-2.5">
          {jobStats.map((s, i) => (
            // Separator trails each item (instead of leading it) so a
            // wrapped line never opens with an orphaned "·" — the dot
            // wraps with the item before it, not the one after.
            <span key={i} className="flex items-center gap-1 text-[12px] min-w-0 max-w-full">
              <span className="text-text-secondary flex-shrink-0">{s.label}:</span>
              <span className="text-text-primary font-semibold break-words">{s.value}</span>
              {i < jobStats.length - 1 && <span className="text-border-primary/60 mx-0.5 select-none">·</span>}
            </span>
          ))}
        </div>

        {/* Description */}
        {job?.role_overview && <p className="mt-2.5 text-[13px] text-text-secondary leading-relaxed line-clamp-3">{job.role_overview}</p>}

        {/* Skills */}
        <OneLineSkills
          skills={skills}
          className="mt-2.5"
          badgeClassName="rounded-md border"
          badgeStyle="px-2 py-2 text-[11px] leading-none"
        />
      </div>

      {/* Submitted Resumes */}
      <PermissionGuard permission={PERMISSIONS.CANDIDATE_VIEW} isComponent>
        <div className="border-t border-border-primary/40 mt-auto">
          <Button
            onClick={handleToggleResumes}
            className="flex items-center justify-between w-full px-4 py-2.5 text-xs hover:bg-background-tertiary/20 transition-colors"
          >
            <span className="flex items-center gap-1.5 font-medium text-text-secondary">
              <FileText size={13} className="flex-shrink-0" />
              <span>Top 5 Resumes</span>
            </span>

            <div className="flex items-center gap-2">
              {resumesOpen && candidatesData[job?.id]?.length >= 5 && (
                <span
                  role="Button"
                  onClick={handleViewDetail}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-border-primary/60 text-text-secondary hover:text-text-primary transition-colors"
                >
                  View All
                </span>
              )}
              {resumesOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </div>
          </Button>

          {resumesOpen && (
            <div className="border-t border-border-primary/30 p-2">
              <CandidateSection
                jobId={job?.id}
                candidates={(candidatesData[job?.id] || [])
                  .slice()
                  .sort((a, b) => (b.match_score ?? 0) - (a.match_score ?? 0))
                  .slice(0, 5)}
                loading={candidatesLoading[job?.id]}
                viewMode={viewMode}
                onViewMore={() => navigate("/admin/candidates", { state: { jobId: job?.id, jobTitle: job?.title } })}
              />
            </div>
          )}
        </div>
      </PermissionGuard>
      {/* The app's own confirm dialog, not a hand-rolled overlay. The card
          version here duplicated `AlertModal` — the component every other
          destructive action on the site already uses — and missed what it
          brings: Escape to dismiss, a focus trap, and the scroll lock. */}
      <AlertModal
        open={showStatusConfirm}
        onOpenChange={(open) => !open && setShowStatusConfirm(false)}
        title={isOpen ? "Close this job?" : "Reopen this job?"}
        description={
          isOpen
            ? "Closing this job will stop it from accepting new applicants. You can reopen it later."
            : "Reopening this job will make it visible and open to new applicants again."
        }
        cancelText="Cancel"
        actionText={isOpen ? "Close Job" : "Reopen Job"}
        loading={isDataUpdate}
        onCancel={() => setShowStatusConfirm(false)}
        onAction={confirmStatusChange}
      />
      <ShareJobQrModal open={shareQrOpen} onOpenChange={setShareQrOpen} jobId={job?.id} jobTitle={job?.title} />
    </Card>
  );
};

export default JobCard;
